import React, { useState } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from 'lucide-react';
import Drawer from './Drawer';

interface UserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active: boolean;
  is_active_member: boolean;
  membership_expiry?: string;
  password: string;
  confirm_password: string;
}

interface AddUserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: UserFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  errors?: Record<string, string>;
}

const inputCls = (hasError?: boolean) =>
  `w-full px-3 py-2 border ${hasError ? 'border-[var(--lib-danger)]' : 'border-[var(--lib-hairline)]'} rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent`;

const labelCls = 'block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5';

const AddUserForm: React.FC<AddUserFormProps> = ({
  open,
  onClose,
  onSubmit,
  onCancel,
  isLoading = false,
  errors = {}
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'student',
    student_id: '',
    employee_id: '',
    phone_number: '',
    date_of_birth: '',
    address: '',
    is_active: true,
    is_active_member: true,
    membership_expiry: '',
    password: '',
    confirm_password: '',
  });

  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    message: string;
    color: string;
  }>({ score: 0, message: '', color: 'bg-[var(--lib-hairline)]' });

  const checkPasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength({ score: 0, message: '', color: 'bg-[var(--lib-hairline)]' });
      return;
    }
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    const map: Record<number, { message: string; color: string }> = {
      0: { message: 'Very Weak', color: 'bg-red-500' },
      1: { message: 'Very Weak', color: 'bg-red-500' },
      2: { message: 'Weak', color: 'bg-orange-500' },
      3: { message: 'Fair', color: 'bg-yellow-500' },
      4: { message: 'Good', color: 'bg-blue-500' },
      5: { message: 'Strong', color: 'bg-green-500' },
    };
    setPasswordStrength({ score, ...map[score] });
  };

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'password') checkPasswordStrength(value);
  };

  const getFieldError = (field: string) => errors[field] || '';

  const isEmployeeType = ['faculty', 'staff', 'librarian', 'admin'].includes(formData.user_type);
  const isStudent = formData.user_type === 'student';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors: Record<string, string> = {};
    if (!formData.username.trim()) validationErrors.username = 'Username is required';
    if (!formData.email.trim()) validationErrors.email = 'Email is required';
    if (!formData.first_name.trim()) validationErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) validationErrors.last_name = 'Last name is required';
    if (!formData.password) validationErrors.password = 'Password is required';
    if (!formData.confirm_password) validationErrors.confirm_password = 'Password confirmation is required';
    if (isStudent && !formData.student_id?.trim()) validationErrors.student_id = 'Student ID is required for student users';
    if (isEmployeeType && !formData.employee_id?.trim()) validationErrors.employee_id = 'Employee ID is required for this user type';

    if (Object.keys(validationErrors).length > 0) {
      console.warn('Form validation errors:', validationErrors);
      return;
    }

    await onSubmit(formData);
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={isLoading}
        className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="add-user-form"
        disabled={isLoading}
        className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors"
      >
        {isLoading ? 'Creating...' : 'Create User'}
      </button>
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} title="Add User" footer={footer} width={520}>
      <form id="add-user-form" onSubmit={handleSubmit} className="space-y-6">

        {/* Basic Information */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Basic Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>First Name <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.first_name}
                  onChange={e => handleInputChange('first_name', e.target.value)}
                  placeholder="First name"
                  className={inputCls(!!getFieldError('first_name'))}
                />
                {getFieldError('first_name') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('first_name')}
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>Last Name <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.last_name}
                  onChange={e => handleInputChange('last_name', e.target.value)}
                  placeholder="Last name"
                  className={inputCls(!!getFieldError('last_name'))}
                />
                {getFieldError('last_name') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('last_name')}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>Username <span className="text-[var(--lib-danger)]">*</span></label>
              <input
                value={formData.username}
                onChange={e => handleInputChange('username', e.target.value)}
                placeholder="Username"
                className={inputCls(!!getFieldError('username'))}
              />
              {getFieldError('username') && (
                <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{getFieldError('username')}
                </p>
              )}
            </div>
            <div>
              <label className={labelCls}>Email Address <span className="text-[var(--lib-danger)]">*</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder="Email address"
                className={inputCls(!!getFieldError('email'))}
              />
              {getFieldError('email') && (
                <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{getFieldError('email')}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Account Settings */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Account Settings</h3>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>User Type <span className="text-[var(--lib-danger)]">*</span></label>
              <select
                value={formData.user_type}
                onChange={e => handleInputChange('user_type', e.target.value)}
                className={inputCls(!!getFieldError('user_type'))}
              >
                <option value="student">Student</option>
                <option value="faculty">Faculty</option>
                <option value="staff">Staff</option>
                <option value="librarian">Librarian</option>
                <option value="admin">Administrator</option>
                <option value="guest">Guest</option>
              </select>
            </div>
            {isStudent && (
              <div>
                <label className={labelCls}>Student ID <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.student_id || ''}
                  onChange={e => handleInputChange('student_id', e.target.value)}
                  placeholder="Student ID"
                  className={inputCls(!!getFieldError('student_id'))}
                />
                {getFieldError('student_id') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('student_id')}
                  </p>
                )}
              </div>
            )}
            {isEmployeeType && (
              <div>
                <label className={labelCls}>Employee ID <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.employee_id || ''}
                  onChange={e => handleInputChange('employee_id', e.target.value)}
                  placeholder="Employee ID"
                  className={inputCls(!!getFieldError('employee_id'))}
                />
                {getFieldError('employee_id') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('employee_id')}
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-col gap-2 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  id="add-is_active"
                  checked={formData.is_active}
                  onCheckedChange={checked => handleInputChange('is_active', !!checked)}
                />
                <span className="text-sm text-[var(--lib-ink)]">Active Account</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  id="add-is_active_member"
                  checked={formData.is_active_member}
                  onCheckedChange={checked => handleInputChange('is_active_member', !!checked)}
                />
                <span className="text-sm text-[var(--lib-ink)]">Active Library Member</span>
              </label>
            </div>
          </div>
        </section>

        {/* Personal Information */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Personal Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Phone Number</label>
                <input
                  value={formData.phone_number || ''}
                  onChange={e => handleInputChange('phone_number', e.target.value)}
                  placeholder="Phone"
                  className={inputCls(!!getFieldError('phone_number'))}
                />
              </div>
              <div>
                <label className={labelCls}>Date of Birth</label>
                <input
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={e => handleInputChange('date_of_birth', e.target.value)}
                  className={inputCls(!!getFieldError('date_of_birth'))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Address</label>
                <input
                  value={formData.address || ''}
                  onChange={e => handleInputChange('address', e.target.value)}
                  placeholder="Address"
                  className={inputCls(!!getFieldError('address'))}
                />
              </div>
              <div>
                <label className={labelCls}>Membership Expiry</label>
                <input
                  type="date"
                  value={formData.membership_expiry || ''}
                  onChange={e => handleInputChange('membership_expiry', e.target.value)}
                  className={inputCls(!!getFieldError('membership_expiry'))}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Password */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">
            Password <span className="text-[var(--lib-danger)] normal-case font-normal text-xs">* Required</span>
          </h3>
          {(getFieldError('password') || getFieldError('confirm_password')) && (
            <div className="mb-3 p-3 bg-[var(--lib-surface-2)] border border-[var(--lib-danger)]/30 rounded-lg text-sm text-[var(--lib-danger)] flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {getFieldError('password') || getFieldError('confirm_password')}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Password <span className="text-[var(--lib-danger)]">*</span></label>
              <input
                type="password"
                value={formData.password}
                onChange={e => handleInputChange('password', e.target.value)}
                placeholder="Password"
                className={inputCls(!!getFieldError('password'))}
              />
              {formData.password && (
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--lib-ink-muted)]">Strength:</span>
                    <span className={`font-medium ${
                      passwordStrength.score <= 2 ? 'text-[var(--lib-danger)]' :
                      passwordStrength.score <= 3 ? 'text-yellow-600' : 'text-green-600'
                    }`}>{passwordStrength.message}</span>
                  </div>
                  <div className="w-full bg-[var(--lib-surface-2)] rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Confirm Password <span className="text-[var(--lib-danger)]">*</span></label>
              <input
                type="password"
                value={formData.confirm_password}
                onChange={e => handleInputChange('confirm_password', e.target.value)}
                placeholder="Confirm password"
                className={inputCls(!!getFieldError('confirm_password'))}
              />
              {formData.confirm_password && formData.password && (
                <p className={`text-xs mt-1 ${formData.password === formData.confirm_password ? 'text-green-600' : 'text-[var(--lib-danger)]'}`}>
                  {formData.password === formData.confirm_password ? '✓ Passwords match' : '✗ Passwords don\'t match'}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-[var(--lib-ink-muted)] mt-3">
            At least 8 characters with uppercase, lowercase, numbers, and special characters.
          </p>
        </section>
      </form>
    </Drawer>
  );
};

export default AddUserForm;
